// Team play: pure helpers shared by the engine, the server and the app
// (docs/team-games-plan.md, docs/setup-redesign/teams-config.md).
//
// Every helper is gated on `state.teams` / `config.teams`. When that is absent
// the game is free-for-all and each helper returns the free-for-all answer:
// `teamOf` is null, `areAllies` is false, `isAlly` / `canPlaceOn` mean "owned by
// me", and `activeTeams` is the active players.

import type { MapDef, PlayerId, TeamId, TeamMembership, TeamSize, TeamsConfig } from './index.js'
import { PLAYER_SEATS_MAX, mapSeatRange } from './seats.js'
import { resignedSlotIds, type PlayerStatusSource } from './playerStatus.js'

/** Team letters in seat order. Four teams is the most the 9-seat cap allows. */
export const TEAM_IDS: readonly TeamId[] = ['A', 'B', 'C', 'D']
/** The team sizes a lobby can choose. */
export const TEAM_SIZES: readonly TeamSize[] = [2, 3, 4]

/**
 * The server's refusal text when bots in team games are rolled back
 * (`BOTS_IN_TEAM_GAMES_ENABLED=0`, docs/deployment.md). Bots join team games
 * normally (docs/bots/team-and-fog-bots-plan.md, decision 3).
 */
export const TEAM_BOTS_NOTE = "Bots can't join team games yet."

// ─── Is this a team game? ───────────────────────────────────────────────────

/**
 * The team format in a setup config (a lobby's `setupConfig`, a create or
 * edit draft's config), or undefined for free-for-all.
 */
export function teamsConfigOf(config: unknown): TeamsConfig | undefined {
  if (!config || typeof config !== 'object') return undefined
  const teams = (config as { teams?: unknown }).teams
  return teams && typeof teams === 'object' ? teams as TeamsConfig : undefined
}

/**
 * True for a team game: the lobby was set up with teams (`setupConfig.teams`)
 * or the game started with them (`state.teams`). One check for every
 * team-only rule that applies from the lobby on, so the server and the app
 * agree (for example: no 1:1 secret messages in team games).
 */
export function isTeamGame(game: { state?: unknown; setupConfig?: unknown } | null | undefined): boolean {
  if (!game) return false
  const teams = (game.state as { teams?: TeamMembership } | null | undefined)?.teams
  return (!!teams && Object.keys(teams).length > 0) || !!teamsConfigOf(game.setupConfig)
}

/** "Team A" … "Team D". */
export function teamLabel(team: TeamId): string {
  return `Team ${team}`
}

/**
 * The mark that identifies a team wherever it appears. Final set (maintainer,
 * 2026-11-01; docs/team-games/team-final-four.html): circle, rounded square,
 * optical-size diamond, optical-size hexagon.
 */
export type TeamShapeName = 'circle' | 'square' | 'diamond' | 'hexagon'

/**
 * Team shapes in team order: Team A is the circle, B the rounded square, C the
 * diamond and D the hexagon. A game with N teams uses the first N, so the
 * hexagon only appears in four-team (2v2v2v2) games.
 */
export const TEAM_SHAPES: readonly TeamShapeName[] = ['circle', 'square', 'diamond', 'hexagon']

/** A team's shape: the shape at the team letter's position in {@link TEAM_IDS}. */
export function teamShape(team: TeamId): TeamShapeName {
  return TEAM_SHAPES[TEAM_IDS.indexOf(team)] ?? 'circle'
}

// ─── Seats and formats ──────────────────────────────────────────────────────

/**
 * The team a seat belongs to under manual assignment. Seats `1..n` are split
 * into contiguous groups of `size`: with teams of 3, seats 1–3 are Team A,
 * 4–6 Team B, 7–9 Team C. Returns null for a seat outside Team A–D.
 */
export function teamForSlot(slot: number, size: TeamSize): TeamId | null {
  if (!Number.isInteger(slot) || slot < 1) return null
  return TEAM_IDS[Math.floor((slot - 1) / size)] ?? null
}

/** One valid team format: `teams` teams of `size`, `players` seats in all. */
export interface TeamFormat {
  size: TeamSize
  players: number
  teams: number
}

type SeatMap = Pick<MapDef, 'territories' | 'fixedSetup'>

/**
 * Every valid team format for a map: equal teams of 2, 3 or 4, at least two
 * teams, at most the 9-seat maximum, and inside the map's seat range
 * (`mapSeatRange`, which carries both territory capacity and a fixed setup's
 * seat counts). Sorted by size, then player count.
 *
 * Most maps: 2v2, 2v2v2, 2v2v2v2, 3v3, 3v3v3, 4v4. Hexcore (2–6 seats): 2v2,
 * 2v2v2, 3v3. Capture the Flag (exactly 4): 2v2 only. With no map, only the
 * seat maximum applies.
 */
export function teamFormats(map?: SeatMap | null): TeamFormat[] {
  const range = map ? mapSeatRange(map) : { min: 2, max: PLAYER_SEATS_MAX }
  const formats: TeamFormat[] = []
  for (const size of TEAM_SIZES) {
    for (let teams = 2; teams <= TEAM_IDS.length; teams++) {
      const players = size * teams
      if (players > PLAYER_SEATS_MAX) break
      if (players >= range.min && players <= range.max) formats.push({ size, players, teams })
    }
  }
  return formats
}

/** Why a team format was rejected. */
export type TeamFormatError =
  | 'invalid-size' // not 2, 3 or 4
  | 'not-equal-teams' // players is not a multiple of size
  | 'too-few-teams' // fewer than two teams
  | 'too-many-seats' // more teams than letters, or over the 9-seat maximum
  | 'map-seats' // outside the map's seat range

export type TeamFormatCheck =
  | { ok: true; format: TeamFormat }
  | { ok: false; error: TeamFormatError; reason: string }

/**
 * Validate a team format against a map: the server's create / setup / settings
 * validation and the client form both call this, so they accept and reject the
 * same inputs. A format is valid exactly when `teamFormats(map)` lists it.
 *
 * `players` is the lobby's `maxPlayers` (a team game always starts full, so it
 * is also `minPlayers`).
 */
export function validateTeamFormat(input: { size: number; players: number; map?: SeatMap | null }): TeamFormatCheck {
  const { size, players, map } = input
  if (!(TEAM_SIZES as readonly number[]).includes(size)) {
    return { ok: false, error: 'invalid-size', reason: 'Teams must have 2, 3 or 4 players' }
  }
  if (!Number.isInteger(players) || players % size !== 0) {
    return { ok: false, error: 'not-equal-teams', reason: `Teams of ${size} need a player count that is a multiple of ${size}` }
  }
  const teams = players / size
  if (teams < 2) {
    return { ok: false, error: 'too-few-teams', reason: `Teams of ${size} need at least ${size * 2} players` }
  }
  if (teams > TEAM_IDS.length || players > PLAYER_SEATS_MAX) {
    return { ok: false, error: 'too-many-seats', reason: `Games seat at most ${PLAYER_SEATS_MAX} players` }
  }
  const format = teamFormats(map).find((f) => f.size === size && f.players === players)
  if (!format) {
    const range = map ? mapSeatRange(map) : { min: 2, max: PLAYER_SEATS_MAX }
    const seats = range.min === range.max ? `exactly ${range.max}` : `${range.min} to ${range.max}`
    return { ok: false, error: 'map-seats', reason: `This map seats ${seats} players` }
  }
  return { ok: true, format }
}

/**
 * The valid player count for teams of `size` closest to `players`, ties going
 * down (teams-config.md "Snapping"). Null when the map allows no format of
 * that size.
 */
export function snapTeamPlayers(map: SeatMap | null | undefined, size: TeamSize, players: number): number | null {
  let best: number | null = null
  for (const f of teamFormats(map)) {
    if (f.size !== size) continue
    if (best === null || Math.abs(f.players - players) < Math.abs(best - players)) best = f.players
  }
  return best
}

/**
 * The seat count a team lobby snaps to on a map for teams of `size`: the
 * closest valid count to `players` (ties going down, {@link snapTeamPlayers}),
 * stepped up to the next valid count when that would drop below `floor` (the
 * highest seated or reserved seat). Null when no count of that size fits.
 * The setup form (`snapTeams`) and the server's lobby edits
 * (`resolveTeamSeats`) both snap with this, so a map change the app accepts
 * is one the server accepts.
 */
export function snapTeamSeats(map: SeatMap | null | undefined, size: TeamSize, players: number, floor = 0): number | null {
  const snapped = snapTeamPlayers(map, size, Math.max(players, floor))
  if (snapped === null) return null
  if (snapped >= floor) return snapped
  return teamFormats(map).find((f) => f.size === size && f.players >= floor)?.players ?? null
}

// ─── In-game predicates ─────────────────────────────────────────────────────

/** The slice of a game state the team predicates read (engine or wire). */
export interface TeamsState {
  teams?: TeamMembership
  players?: readonly PlayerId[]
}

/** A player's team, or null in free-for-all (or for a player with no team). */
export function teamOf(state: TeamsState | null | undefined, playerId: PlayerId | null | undefined): TeamId | null {
  if (!state?.teams || playerId == null) return null
  return state.teams[playerId] ?? null
}

/**
 * True when `a` and `b` are different players on the same team. Always false
 * in free-for-all.
 */
export function areAllies(state: TeamsState | null | undefined, a: PlayerId | null | undefined, b: PlayerId | null | undefined): boolean {
  if (a == null || b == null || a === b) return false
  const team = teamOf(state, a)
  return team !== null && team === teamOf(state, b)
}

/**
 * True when `ownerId` is on my side: me, or a teammate. In free-for-all this
 * is exactly `ownerId === myPlayerId`. The app uses it wherever it used to
 * compare a territory's owner with the viewer (placement and fortify targets).
 * Neutral or unowned territory ('' / undefined) is never an ally.
 */
export function isAlly(state: TeamsState | null | undefined, myPlayerId: PlayerId | null | undefined, ownerId: PlayerId | null | undefined): boolean {
  if (!ownerId || myPlayerId == null) return false
  return ownerId === myPlayerId || areAllies(state, myPlayerId, ownerId)
}

/**
 * May `playerId` place reinforcements on (or fortify into) this territory?
 * Own territory always; a teammate's when teams are on. Troop caps are a
 * separate check (`armyHeadroom`).
 */
export function canPlaceOn(state: TeamsState | null | undefined, playerId: PlayerId, territory: { ownerId?: PlayerId | null }): boolean {
  return isAlly(state, playerId, territory.ownerId)
}

/**
 * The sides still in the game, given the players still in it: their distinct
 * teams in order of first appearance, or the players themselves in
 * free-for-all. A player with no team entry counts as a side of their own.
 * The game ends when one side (or none) is left.
 */
export function activeTeams(state: TeamsState | null | undefined, activePlayers: readonly PlayerId[]): TeamId[] | PlayerId[] {
  if (!state?.teams) return [...activePlayers]
  const sides: string[] = []
  for (const p of activePlayers) {
    const side = teamOf(state, p) ?? p
    if (!sides.includes(side)) sides.push(side)
  }
  return sides
}

/**
 * Every member of `team` in turn order (eliminated members included), from
 * `state.players` when present. Empty in free-for-all.
 */
export function teamMembers(state: TeamsState | null | undefined, team: TeamId): PlayerId[] {
  const teams = state?.teams
  if (!teams) return []
  const order = state.players ?? Object.keys(teams)
  return order.filter((p) => teams[p] === team)
}

// ─── Team chat ──────────────────────────────────────────────────────────────

/** A game seat as team chat reads it (a `GamePlayer` row or its wire form). */
export interface TeamChatSeat {
  userId: string
  slot: number
  isBot: boolean
}

/** The state team chat reads: who is on which team, and who resigned. */
export interface TeamChatState extends TeamsState, PlayerStatusSource {}

/**
 * The team whose secret chat `userId` may read and post in, or null.
 *
 * One channel per team per game. A member is a human seat on that team that
 * has not resigned. Members knocked out in combat keep their seat in the
 * chat, so they can still coach. Null in free-for-all, for bots, for
 * resigned players and for anyone without a seat (spectators, players whose
 * seat an AI took over). The server enforces this and the app uses it to
 * decide whether to show the team chat at all, so both agree.
 */
export function teamChatTeam(
  state: TeamChatState | null | undefined,
  seats: readonly TeamChatSeat[],
  userId: string | null | undefined,
): TeamId | null {
  if (!userId) return null
  const seat = seats.find((s) => s.userId === userId && !s.isBot)
  if (!seat) return null
  const playerId = String(seat.slot)
  if (resignedSlotIds(state ?? undefined).has(playerId)) return null
  return teamOf(state, playerId)
}

/**
 * Every user who may read `team`'s chat (see {@link teamChatTeam}): the
 * delivery list for a new team message. In seat order.
 */
export function teamChatMembers<S extends TeamChatSeat>(
  state: TeamChatState | null | undefined,
  seats: readonly S[],
  team: TeamId,
): S[] {
  return [...seats]
    .sort((a, b) => a.slot - b.slot)
    .filter((s) => teamChatTeam(state, [s], s.userId) === team)
}
